import { useEffect, useState, type FormEvent } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { parentClaimOpen } from "../domain/auth";
import { gradeLabel } from "../domain/data";
import { formatPhoneInput } from "../domain/phone";
import type { KidDecision } from "../domain/parentClaim";
import { publicSupabaseConfig } from "../domain/env";
import { SIGN_UP_PASSWORD_MIN } from "../domain/supabaseAuth";
import { useAuth } from "../state/AuthProvider";
import { useLocale } from "../state/i18n";
import { useStudents } from "../state/students";
import { ErrorFlash } from "../components/ErrorFlash";
import { PasswordInput } from "../components/PasswordInput";
import { PhoneInput } from "../components/PhoneInput";
import { PrototypeBanner, PublicNav, SignOutButton } from "../components/ui";

export function ParentClaim() {
  const navigate = useNavigate();
  const { account, loading, finishParentClaim } = useAuth();
  const { getStudent } = useStudents();
  const { t } = useLocale();
  const hosted = publicSupabaseConfig() !== null;
  const minLength = hosted ? SIGN_UP_PASSWORD_MIN : 4;

  const [given, setGiven] = useState(account?.given ?? "");
  const [family, setFamily] = useState(account?.family ?? "");
  const [phone, setPhone] = useState(() => formatPhoneInput(account?.phone ?? ""));
  const [seeded, setSeeded] = useState(Boolean(account?.email));

  useEffect(() => {
    if (!account || seeded) return;
    setGiven(account.given);
    setFamily(account.family);
    setPhone(formatPhoneInput(account.phone));
    setSeeded(true);
  }, [account, seeded]);
  const [confirmed, setConfirmed] = useState(false);
  const [decisions, setDecisions] = useState<Record<string, KidDecision | undefined>>({});
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) {
    return (
      <div className="app">
        <PrototypeBanner />
        <main className="shell">
          <p className="anno">Loading...</p>
        </main>
      </div>
    );
  }

  if (!account) return <Navigate to="/signin" replace />;
  if (!parentClaimOpen(account)) return <Navigate to="/family" replace />;

  const pending = account.pendingStudentIds;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const result = await finishParentClaim({
      given,
      family,
      phone,
      confirmed,
      password,
      confirmPassword,
      decisions,
    });
    setBusy(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    navigate("/family", { replace: true });
  }

  return (
    <div className="app">
      <PrototypeBanner />
      <main className="shell">
        <PublicNav />
        <div className="auth-panel">
          <p className="meta">{t("claim.meta")}</p>
          <h1>{t("claim.title")}</h1>
          <p className="lede">{t("claim.lede")}</p>
          <form className="stack" onSubmit={onSubmit}>
            <div className="wire">
              <div className="meta">{t("claim.details")}</div>
              <p className="form-note">{t("claim.detailsHint")}</p>
              <div className="field">
                <label htmlFor="claim-given">{t("claim.given")}</label>
                <input
                  id="claim-given"
                  value={given}
                  onChange={(event) => setGiven(event.target.value)}
                  autoComplete="given-name"
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="claim-family">{t("claim.family")}</label>
                <input
                  id="claim-family"
                  value={family}
                  onChange={(event) => setFamily(event.target.value)}
                  autoComplete="family-name"
                  required
                />
              </div>
              <div className="field">
                <label htmlFor="claim-email">{t("claim.email")}</label>
                <input id="claim-email" value={account.email} readOnly />
                <div className="anno">{t("claim.emailNote")}</div>
              </div>
              <div className="field">
                <label htmlFor="claim-phone">{t("claim.phone")}</label>
                <PhoneInput id="claim-phone" value={phone} onChange={setPhone} required />
              </div>
              <div className="checks" style={{ marginTop: "0.75rem" }}>
                <label>
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(event) => setConfirmed(event.target.checked)}
                    required
                  />
                  <span>{t("claim.confirm")}</span>
                </label>
              </div>
            </div>

            {pending.length > 0 ? (
              <div className="wire">
                <div className="meta">{t("claim.kids")}</div>
                <p className="form-note">{t("claim.kidsHint")}</p>
                <ul className="list-plain">
                  {pending.map((id) => {
                    const student = getStudent(id);
                    const name = student
                      ? `${student.given} ${student.family}`
                      : "Assigned student";
                    const given = student?.given || name;
                    return (
                      <li key={id}>
                        <div className="title">
                          <strong>{name}</strong>
                        </div>
                        {student ? (
                          <div className="anno">
                            {student.school} · Grade {gradeLabel(student.grade)}
                          </div>
                        ) : null}
                        <div className="checks" style={{ marginTop: "0.45rem" }}>
                          <label>
                            <input
                              type="radio"
                              name={`claim-${id}`}
                              checked={decisions[id] === "accept"}
                              onChange={() =>
                                setDecisions((current) => ({ ...current, [id]: "accept" }))
                              }
                              required
                            />
                            <span>{t("claim.accept", { name: given })}</span>
                          </label>
                          <label>
                            <input
                              type="radio"
                              name={`claim-${id}`}
                              checked={decisions[id] === "decline"}
                              onChange={() =>
                                setDecisions((current) => ({ ...current, [id]: "decline" }))
                              }
                            />
                            <span>{t("claim.decline", { name: given })}</span>
                          </label>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}

            {account.mustResetPassword ? (
              <>
                <p className="form-note">{t("claim.passwordHint")}</p>
                <div className="field">
                  <label htmlFor="claim-password">{t("claim.password")}</label>
                  <PasswordInput
                    id="claim-password"
                    autoComplete="new-password"
                    value={password}
                    minLength={minLength}
                    onChange={setPassword}
                    required
                  />
                </div>
                <div className="field">
                  <label htmlFor="claim-confirm">{t("claim.confirmPassword")}</label>
                  <PasswordInput
                    id="claim-confirm"
                    autoComplete="new-password"
                    value={confirmPassword}
                    minLength={minLength}
                    onChange={setConfirmPassword}
                    required
                  />
                </div>
              </>
            ) : null}

            {error ? <ErrorFlash key={error} message={error} /> : null}
            <div className="actions">
              <button type="submit" disabled={busy}>
                {busy ? t("claim.busy") : t("claim.submit")}
              </button>
              <SignOutButton className="ghost" />
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
